/**
 * 주행거리 관리 API 클라이언트.
 *
 * 데모 API 서버(server/)를 호출한다. CLOVA OCR 은 서버가 대신 호출하므로
 * 이 파일 어디에도 OCR Secret Key / Invoke URL 이 등장하지 않는다.
 */

const API_BASE = '/api/odometer';

/** App.tsx 헤더에 표시되는 데모 로그인 사용자. 서버 사용자 레지스트리와 값이 일치해야 한다. */
export const DEMO_USER_ID = 'charles.lee@prediction.im';

export type OdometerCycle = 'NONE' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
export type OdometerInputMethod = 'MANUAL' | 'OCR_ASSISTED';

export interface OdometerVehicleStatus {
    vin: string;
    vehicleNumber: string;
    model: string | null;
    depot: string | null;
    baselineKm: number;
    latestOdometerKm: number | null;
    latestMeasuredAt: string | null;
    latestInputMethod: OdometerInputMethod | null;
    latestRegisteredByName: string | null;
    readingCount: number;
    cycle: OdometerCycle;
    cycleLabel: string;
    anchorDate: string | null;
    nextDueDate: string | null;
    inputDue: boolean;
    overdueDays: number;
    /** 직전 누적거리 — 역행 입력을 화면에서 즉시 경고하는 데 쓴다. */
    previousKm: number;
}

export interface OdometerReading {
    readingId: string;
    vin: string;
    vehicleNumber: string;
    odometerKm: number;
    measuredAt: string;
    inputMethod: OdometerInputMethod;
    photoFileName: string | null;
    photoSizeBytes: number | null;
    photoMimeType: string | null;
    ocrCandidateKm: number | null;
    ocrConfidence: number | null;
    ocrRawText: string | null;
    registeredBy: string;
    registeredByName: string;
    memo: string | null;
    createdDate: string;
}

export interface OdometerSchedule {
    vin: string;
    cycle: OdometerCycle;
    cycleLabel: string;
    anchorDate: string;
    nextDueDate: string;
    enabled: boolean;
}

export interface OdometerAlarm {
    alarmId: string;
    vin: string;
    vehicleNumber: string;
    dueDate: string;
    cycle: OdometerCycle;
    cycleLabel: string;
    message: string;
    confirmed: boolean;
    createdDate: string;
}

export interface OcrCandidate {
    odometerKm: number;
    rawText: string;
    confidence: number;
    score: number;
    matchedKeyword: string | null;
    fieldName: string | null;
}

export interface OcrPhotoMeta {
    fileName: string;
    sizeBytes: number;
    mimeType: string;
    sha256: string;
}

export interface OcrRecognizeResult {
    requestId: string | null;
    candidates: OcrCandidate[];
    bestCandidate: OcrCandidate | null;
    /** 후보가 없거나 신뢰도가 기준 미만 — 화면은 직접 입력을 안내한다. */
    lowConfidence: boolean;
    recognizedText: string;
    minConfidence: number;
    /** OCR 자체가 불가능한 경우의 사유 (미설정, 네트워크 장애 등) */
    unavailableReason?: string;
    photo: OcrPhotoMeta;
}

export interface OcrStatus {
    configured: boolean;
    minConfidence: number;
    allowedExtensions: string[];
    maxBytes: number;
}

/**
 * 화면이 구분해야 하는 세 가지 상태.
 *
 * `unknown` 이 없으면 API 서버에 닿지 못한 경우까지 "OCR 미설정" 으로 표시된다.
 * 원인이 전혀 다른데 같은 안내가 나가면 운영자가 자격증명만 계속 확인하게 된다.
 */
export type OcrAvailability = 'unknown' | 'configured' | 'unconfigured';

export interface CreateReadingInput {
    vin: string;
    odometerKm: number;
    measuredAt: string;
    inputMethod: OdometerInputMethod;
    memo?: string;
    photo?: OcrPhotoMeta | null;
    ocr?: {
        candidateKm: number | null;
        confidence: number | null;
        requestId: string | null;
        rawText: string | null;
    } | null;
}

/** 서버가 내려준 사용자용 메시지를 그대로 담는 오류. */
export class OdometerApiError extends Error {
    readonly status: number;

    readonly details?: Record<string, unknown>;

    constructor(status: number, message: string, details?: Record<string, unknown>) {
        super(message);
        this.name = 'OdometerApiError';
        this.status = status;
        this.details = details;
    }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    let response: Response;
    try {
        response = await fetch(`${API_BASE}${path}`, {
            ...init,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                // 데모 로그인 사용자. 서버가 등록자·권한을 확정하는 데 쓴다.
                'X-Demo-User': DEMO_USER_ID,
                ...(init?.headers || {}),
            },
        });
    } catch {
        throw new OdometerApiError(
            0,
            '데모 API 서버에 연결할 수 없습니다. `npm run server` 실행 여부를 확인해 주세요.'
        );
    }

    const contentType = response.headers.get('Content-Type') || '';
    if (!contentType.includes('application/json')) {
        // 정적 배포에서 흔한 실패다. 웹서버에 /api 리버스 프록시가 없으면 SPA 폴백이
        // index.html 을 200 으로 돌려주므로 "서버가 죽었다" 와 증상이 똑같아 보인다.
        // 둘을 구분해 줘야 어디를 고쳐야 하는지 알 수 있다.
        throw new OdometerApiError(
            response.status,
            contentType.includes('text/html')
                ? '데모 API 서버로 요청이 전달되지 않았습니다. 웹서버가 /api 요청에 정적 페이지를 돌려주고 있습니다 — /api 리버스 프록시 설정과 API 서버(:4000) 기동 여부를 확인해 주세요.'
                : '데모 API 서버 응답이 올바르지 않습니다. 서버가 실행 중인지 확인해 주세요.'
        );
    }

    const payload = await response.json();
    if (!response.ok) {
        throw new OdometerApiError(
            response.status,
            payload?.message || '요청을 처리하지 못했습니다.',
            payload?.details
        );
    }
    return payload as T;
}

export const odometerApi = {
    /** 데모 차량 목록을 서버에 등록하고 최신 상태를 받아온다. */
    syncVehicles: (
        vehicles: Array<{
            vin: string;
            vehicleNumber: string;
            model?: string;
            depot?: string;
            baselineKm: number;
        }>
    ) =>
        request<{ syncedCount: number; vehicles: OdometerVehicleStatus[] }>('/vehicles/sync', {
            method: 'POST',
            body: JSON.stringify({ vehicles }),
        }),

    listVehicles: () => request<{ vehicles: OdometerVehicleStatus[] }>('/vehicles'),

    listReadings: (vin: string, limit = 100) =>
        request<{ vin: string; readings: OdometerReading[] }>(
            `/readings?vin=${encodeURIComponent(vin)}&limit=${limit}`
        ),

    createReading: (input: CreateReadingInput) =>
        request<{ reading: OdometerReading; previousKm: number; vehicles: OdometerVehicleStatus[] }>(
            '/readings',
            { method: 'POST', body: JSON.stringify(input) }
        ),

    upsertSchedule: (vin: string, cycle: OdometerCycle, anchorDate: string) =>
        request<{ schedule: OdometerSchedule; vehicles: OdometerVehicleStatus[] }>(
            `/schedules/${encodeURIComponent(vin)}`,
            { method: 'PUT', body: JSON.stringify({ cycle, anchorDate }) }
        ),

    listAlarms: () => request<{ alarms: OdometerAlarm[] }>('/alarms'),

    confirmAlarm: (alarmId: string) =>
        request<{ alarms: OdometerAlarm[] }>(`/alarms/${encodeURIComponent(alarmId)}/confirm`, {
            method: 'POST',
        }),

    ocrStatus: () => request<OcrStatus>('/ocr/status'),

    /** 계기판 사진을 서버로 보내 주행거리 후보를 받는다. 저장은 하지 않는다. */
    recognizePhoto: (photo: { fileName: string; data: string }) =>
        request<OcrRecognizeResult>('/ocr', { method: 'POST', body: JSON.stringify({ photo }) }),
};
